import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, SETTING_TYPE, useGetGlobalSettingsQuery } from '@circuitry-ai/doc-data';
import { CtyButton } from '../button/CtyButton';
import { useTheme } from '../context/ThemeContext';

/**
 * Single theme icon in the app header. Shows the current mode and toggles
 * between light and dark when the tenant "Dark Mode" capability is active.
 */
export function ThemeModeSwitcher() {
  const { t } = useTranslation();
  const { isDark, toggle, isDarkModeFeatureEnabled, setDarkModeFeatureEnabled } = useTheme();
  const token = useSelector((state: RootState) => state.auth.token);
  const { data: settings, isSuccess, isError } = useGetGlobalSettingsQuery(undefined, {
    skip: !token,
  });

  const isDarkModeEnabled = useMemo(
    () => Boolean(settings?.find((item) => item.ability === SETTING_TYPE.DARK_MODE && item.is_active)),
    [settings]
  );

  const settingsResolved = isSuccess || isError;

  useEffect(() => {
    if (!token || !settingsResolved) return;
    setDarkModeFeatureEnabled(isDarkModeEnabled);
  }, [token, settingsResolved, isDarkModeEnabled, setDarkModeFeatureEnabled]);

  if (token && (!settingsResolved || !isDarkModeFeatureEnabled)) return null;

  const label = isDark ? t('Dark') : t('Light');

  return (
    <CtyButton
      isIconOnly
      size="sm"
      variant="light"
      className="h-10 w-10 min-w-10 rounded-full hover:bg-[#F4F4F5] dark:hover:bg-content2"
      title={label}
      tooltipContent={label}
      aria-label={label}
      onPress={toggle}
    >
      <Icon
        icon={isDark ? 'lucide:moon' : 'lucide:sun'}
        width={17}
        height={17}
        className="text-[#181818] dark:text-[var(--cty-text-secondary)]"
      />
    </CtyButton>
  );
}

export default ThemeModeSwitcher;
